/*
 * Click-to-navigate by halves: a click on an element's left half means
 * "previous", on its right half "next". Optionally shows an arrow cursor
 * pointing the way a click will go.
 *
 * This is only the gesture. What "previous" and "next" actually do is the
 * caller's business, so the same helper drives both kinds of carousel here:
 *
 *   slick sliders   slickClickNav() in src/js/app.js   → slickPrev / slickNext
 *   .scroller-track bindTrackClick() in scroller.js    → a smooth scrollBy
 *
 * The cursors are .slider-cursor-prev / .slider-cursor-next in
 * src/css/click-nav.css, which needs /img/core/arrow-left.svg and
 * arrow-right.svg.
 */

const CURSOR_PREV = "slider-cursor-prev";
const CURSOR_NEXT = "slider-cursor-next";

/**
 * @param {HTMLElement} el  the element whose halves are clickable
 * @param {object}   options
 * @param {Function} options.onPrev  called on a left-half click
 * @param {Function} options.onNext  called on a right-half click
 * @param {boolean}  [options.cursor=true]  show the arrow cursor on hover
 * @param {Function} [options.canGo]  (direction: "prev" | "next") => boolean.
 *   Return false when there is nowhere to go that way — at the end of a
 *   scroller, say — and that half shows the normal cursor and ignores clicks.
 *   Defaults to always true, which suits looping sliders.
 */
export function clickNav(el, { onPrev, onNext, cursor = true, canGo = () => true }) {
  /** Which way a pointer event points, or null if that way is closed. */
  const directionOf = (event) => {
    const direction = isLeftHalf(el, event) ? "prev" : "next";
    return canGo(direction) ? direction : null;
  };

  el.addEventListener("click", (event) => {
    // A click that ends a text selection is someone copying, not navigating.
    if (hasTextSelection()) {
      return;
    }

    const direction = directionOf(event);

    if (direction === "prev") onPrev();
    if (direction === "next") onNext();
  });

  if (!cursor) {
    return;
  }

  el.addEventListener("mousemove", (event) => setCursor(el, directionOf(event)));
  el.addEventListener("mouseleave", () => setCursor(el, null));
}

/** True when the event happened left of the element's vertical midline. */
function isLeftHalf(el, event) {
  const rect = el.getBoundingClientRect();
  return event.clientX < rect.left + rect.width / 2;
}

/** Show the arrow for `direction`, or neither arrow for null. */
function setCursor(el, direction) {
  el.classList.toggle(CURSOR_PREV, direction === "prev");
  el.classList.toggle(CURSOR_NEXT, direction === "next");
}

function hasTextSelection() {
  const selection = window.getSelection();
  return selection !== null && selection.toString() !== "";
}
